import { Badge } from "@components/ui/Badge"
import { RowActions } from "@components/ui/RowActions"
import { Table } from "@components/ui/Table"
import { Pencil, RotateCcw, Trash2 } from "lucide-react"
import { memo } from "react"
import { useTranslation } from "react-i18next"

import type { Subject } from "../types"

interface Props {
  subjects: Subject[]
  caption: string
  /** SubjectOut carries no `deleted_at` — the page diffs against the active
   *  list and passes the resulting ids. */
  deletedIds: Set<string>
  canManage: boolean
  onEdit: (subject: Subject) => void
  onTrash: (subject: Subject) => void
  onRestore: (subject: Subject) => void
}

export const SubjectsTable = memo(function SubjectsTable({
  subjects,
  caption,
  deletedIds,
  canManage,
  onEdit,
  onTrash,
  onRestore,
}: Props) {
  const { t } = useTranslation(["schools", "common"])
  return (
    <Table
      caption={caption}
      rows={subjects}
      rowKey={(s) => s.id}
      identity={{
        header: t("subjects.fields.name"),
        // Only the exception is marked. "Estándar" on nine rows out of ten
        // said nothing, in the same grey pill as "En papelera".
        cell: (s) => (
          <span className="flex flex-wrap items-center gap-2">
            <span className="text-ink font-medium">{s.name}</span>
            {!s.default_code && <Badge variant="outline">{t("subjects.badge.custom")}</Badge>}
            {deletedIds.has(s.id) && <Badge variant="neutral">{t("common:badge.deleted")}</Badge>}
          </span>
        ),
      }}
      columns={[]}
      actions={
        canManage
          ? (s) =>
              deletedIds.has(s.id) ? (
                <RowActions
                  subject={s.name}
                  primary={{
                    label: t("actions.restore"),
                    icon: <RotateCcw className="h-4 w-4" />,
                    onClick: () => onRestore(s),
                  }}
                />
              ) : (
                <RowActions
                  subject={s.name}
                  primary={{
                    label: t("subjects.actions.rename"),
                    icon: <Pencil className="h-4 w-4" />,
                    onClick: () => onEdit(s),
                  }}
                  items={[
                    {
                      label: t("actions.trash"),
                      icon: <Trash2 className="h-4 w-4" />,
                      onClick: () => onTrash(s),
                      danger: true,
                    },
                  ]}
                />
              )
          : undefined
      }
      actionsHeader={t("fields.actions")}
    />
  )
})
